/**
 * UpgradeOffer Component
 *
 * The web upgrade offer. Rendered only where COMMERCE_ENABLED — see UpgradePrompt.tsx.
 * Motivational prompt showing what's unlocked in the next tier.
 * Used to encourage users to upgrade.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Sparkles, Check, ArrowRight, Crown, Zap, Star } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import type { StartupTier, InvestorTier } from '@/lib/subscription/types';
import { STARTUP_TIERS, INVESTOR_TIERS } from '@/lib/subscription/types';
import { getStartupUpgradeFeatures, getInvestorUpgradeFeatures, FEATURE_INFO } from '@/lib/subscription/features';
import { formatPrice } from '@/lib/subscription/pricing';

// ============================================================================
// Types
// ============================================================================

export interface UpgradeOfferProps {
  targetTier?: StartupTier | InvestorTier;
  variant?: 'banner' | 'card' | 'fullscreen';
  headline?: string;
  maxFeatures?: number;
  showPrice?: boolean;
}

// ============================================================================
// Tier Order for Next Tier Logic
// ============================================================================

const STARTUP_TIER_ORDER: StartupTier[] = ['free', 'starter', 'standard', 'premium'];
const INVESTOR_TIER_ORDER: InvestorTier[] = ['free', 'starter', 'standard', 'premium'];

function getNextTier(
  currentTier: StartupTier | InvestorTier,
  userRole: 'startup' | 'investor'
): StartupTier | InvestorTier | null {
  const order: readonly string[] = userRole === 'startup' ? STARTUP_TIER_ORDER : INVESTOR_TIER_ORDER;
  const currentIndex = order.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex >= order.length - 1) {
    return null;
  }
  return order[currentIndex + 1] as StartupTier | InvestorTier;
}

// ============================================================================
// Component
// ============================================================================

export function UpgradeOffer({
  targetTier,
  variant = 'card',
  headline,
  maxFeatures = 3,
  showPrice = true,
}: UpgradeOfferProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const userRole = useSubscriptionStore((s) => s.userRole);
  const currentTier = useSubscriptionStore((s) => s.getCurrentTier());

  // Admin users don't need upgrade prompts
  if (userRole === 'admin') return null;

  // Determine target tier (next tier if not specified)
  const effectiveTarget =
    targetTier ||
    (userRole ? getNextTier(currentTier, userRole) : null);

  // No upgrade available if already at top tier
  if (!effectiveTarget || !userRole) {
    return null;
  }

  // Get tier config
  const tierConfig =
    userRole === 'startup'
      ? STARTUP_TIERS[effectiveTarget as StartupTier]
      : INVESTOR_TIERS[effectiveTarget as InvestorTier];

  // Get features unlocked by upgrading
  const unlockedFeatures =
    userRole === 'startup'
      ? getStartupUpgradeFeatures(currentTier as StartupTier, effectiveTarget as StartupTier)
      : getInvestorUpgradeFeatures(currentTier as InvestorTier, effectiveTarget as InvestorTier);

  const displayFeatures = unlockedFeatures.slice(0, maxFeatures);

  const handleUpgrade = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (userRole === 'startup') {
      router.push('/(startup)/startup-subscription');
    } else {
      router.push('/(investor)/investor-subscription');
    }
  };

  // Banner variant - compact horizontal bar
  if (variant === 'banner') {
    return (
      <Animated.View entering={FadeInUp.duration(400)}>
        <AnimatedPressable onPress={handleUpgrade}>
          <LinearGradient
            colors={[colors.primary[500], colors.primary[600]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.bannerContainer}
          >
            <View style={styles.bannerLeft}>
              <Sparkles size={18} color="#fff" />
              <Text style={styles.bannerText}>
                {headline || `Upgrade to ${tierConfig?.name}`}
              </Text>
            </View>
            <View style={styles.bannerRight}>
              {showPrice && tierConfig && (
                <Text style={styles.bannerPrice}>
                  {formatPrice(tierConfig.monthlyPrice)}/mo
                </Text>
              )}
              <ArrowRight size={18} color="#fff" />
            </View>
          </LinearGradient>
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // Fullscreen variant - large promotional card
  if (variant === 'fullscreen') {
    return (
      <Animated.View entering={FadeInUp.duration(500)} style={styles.fullscreenContainer}>
        <LinearGradient
          colors={[colors.primary[50], `${colors.primary[100]}50`]}
          style={styles.fullscreenGradient}
        >
          {/* Crown icon */}
          <View style={[styles.fullscreenIcon, { backgroundColor: colors.primary[100] }]}>
            <Crown size={40} color={colors.primary[600]} />
          </View>

          {/* Headline */}
          <Text style={[styles.fullscreenTitle, { color: colors.text.primary }]}>
            {headline || `Unlock ${tierConfig?.name}`}
          </Text>
          <Text style={[styles.fullscreenSubtitle, { color: colors.text.secondary }]}>
            {tierConfig?.description}
          </Text>

          {/* Features list */}
          <View style={styles.fullscreenFeatures}>
            {displayFeatures.map((feature, index) => {
              const info = FEATURE_INFO[feature];
              return (
                <Animated.View
                  key={feature}
                  entering={FadeInUp.delay(100 + index * 100).duration(400)}
                  style={styles.fullscreenFeatureRow}
                >
                  <View
                    style={[
                      styles.fullscreenFeatureCheck,
                      { backgroundColor: colors.traffic.greenLight },
                    ]}
                  >
                    <Check size={14} color={colors.success} />
                  </View>
                  <Text style={[styles.fullscreenFeatureText, { color: colors.text.primary }]}>
                    {info?.name || feature}
                  </Text>
                </Animated.View>
              );
            })}
            {unlockedFeatures.length > maxFeatures && (
              <Text style={[styles.fullscreenMoreText, { color: colors.text.tertiary }]}>
                +{unlockedFeatures.length - maxFeatures} more features
              </Text>
            )}
          </View>

          {/* Price */}
          {showPrice && tierConfig && (
            <View style={styles.fullscreenPriceContainer}>
              <Text style={[styles.fullscreenPriceLabel, { color: colors.text.tertiary }]}>
                Starting at
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                <Text style={[styles.fullscreenPrice, { color: colors.text.primary }]}>
                  {formatPrice(tierConfig.monthlyPrice)}
                </Text>
                <Text style={[styles.fullscreenPricePeriod, { color: colors.text.tertiary }]}>
                  /month
                </Text>
              </View>
            </View>
          )}

          {/* CTA Button */}
          <Pressable
            onPress={handleUpgrade}
            style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1, width: '100%' })}
          >
            <LinearGradient
              colors={[colors.primary[500], colors.primary[600]]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.fullscreenButton}
            >
              <Zap size={18} color="#fff" />
              <Text style={styles.fullscreenButtonText}>
                Upgrade to {tierConfig?.name}
              </Text>
            </LinearGradient>
          </Pressable>
        </LinearGradient>
      </Animated.View>
    );
  }

  // Card variant (default)
  return (
    <Animated.View entering={FadeInUp.duration(400)}>
      <View
        style={[
          styles.cardContainer,
          {
            backgroundColor: colors.surface.primary,
            borderColor: colors.primary[200],
          },
        ]}
      >
        {/* Header */}
        <View style={styles.cardHeader}>
          <View style={[styles.cardIconBg, { backgroundColor: colors.primary[100] }]}>
            <Star size={20} color={colors.primary[600]} />
          </View>
          <View style={styles.cardHeaderText}>
            <Text style={[styles.cardTitle, { color: colors.text.primary }]}>
              {headline || `Upgrade to ${tierConfig?.name}`}
            </Text>
            <Text style={[styles.cardSubtitle, { color: colors.text.secondary }]}>
              Unlock more features
            </Text>
          </View>
          {showPrice && tierConfig && (
            <View style={styles.cardPriceTag}>
              <Text style={[styles.cardPrice, { color: colors.primary[600] }]}>
                {formatPrice(tierConfig.monthlyPrice)}
              </Text>
              <Text style={[styles.cardPricePeriod, { color: colors.text.tertiary }]}>
                /mo
              </Text>
            </View>
          )}
        </View>

        {/* Features */}
        <View style={styles.cardFeatures}>
          {displayFeatures.map((feature) => {
            const info = FEATURE_INFO[feature];
            return (
              <View key={feature} style={styles.cardFeatureRow}>
                <Check size={14} color={colors.success} />
                <Text style={[styles.cardFeatureText, { color: colors.text.secondary }]}>
                  {info?.name || feature}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Button */}
        <AnimatedPressable
          onPress={handleUpgrade}
          style={[
            styles.cardButton,
            {
              backgroundColor: colors.primary[500],
            },
          ]}
        >
          <Text style={styles.cardButtonText}>View Plans</Text>
          <ArrowRight size={16} color="#fff" />
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  // Banner variant
  bannerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 14,
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  bannerText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  bannerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bannerPrice: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 13,
    fontWeight: '500',
  },

  // Fullscreen variant
  fullscreenContainer: {
    flex: 1,
    padding: 24,
  },
  fullscreenGradient: {
    flex: 1,
    borderRadius: 24,
    padding: 32,
    alignItems: 'center',
  },
  fullscreenIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  fullscreenTitle: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  fullscreenSubtitle: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 32,
  },
  fullscreenFeatures: {
    width: '100%',
    marginBottom: 32,
  },
  fullscreenFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  fullscreenFeatureCheck: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullscreenFeatureText: {
    fontSize: 15,
    fontWeight: '500',
  },
  fullscreenMoreText: {
    fontSize: 13,
    marginTop: 8,
    marginLeft: 40,
  },
  fullscreenPriceContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  fullscreenPriceLabel: {
    fontSize: 13,
    marginBottom: 4,
  },
  fullscreenPrice: {
    fontSize: 40,
    fontWeight: '700',
  },
  fullscreenPricePeriod: {
    fontSize: 16,
    marginLeft: 4,
  },
  fullscreenButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 14,
    gap: 8,
  },
  fullscreenButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Card variant
  cardContainer: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardIconBg: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardHeaderText: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  cardSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  cardPriceTag: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  cardPrice: {
    fontSize: 18,
    fontWeight: '700',
  },
  cardPricePeriod: {
    fontSize: 12,
    marginLeft: 2,
  },
  cardFeatures: {
    marginBottom: 16,
    paddingLeft: 4,
  },
  cardFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  cardFeatureText: {
    fontSize: 13,
  },
  cardButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    gap: 6,
  },
  cardButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});
